import {createStyles, Text, Textarea} from '@mantine/core';
import React, {useState} from 'react';

import {updateAnalysisOptimistically} from 'analysis-tool/AnalysisActions';
import {UpdateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {MAX_ANALYSIS_DESCRIPTION_LENGTH} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import BenchlingConfig from 'BenchlingConfig';
import FolderSelector from 'file-browser/FolderSelector';
import clampEntryName from 'notebook/entry-templates/clampEntryName';
import EditableStudiesList from 'studies/studyable/EditableStudiesList';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useFuzzyDate from 'ui-library/hooks/useFuzzyDate';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {readableTime} from 'util/DateUtil';
import {capitalize} from 'util/NameUtil';

import DataSharingWarningBanner from '../components/DataSharingWarningBanner';
import AnalysisRelevantItemsSection from './AnalysisRelevantItemsSection';

export interface AnalysisMetadataModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  analysis: AnalysisGraphQL;
}

const useStyles = createStyles(() => ({
  input: {
    resize: 'vertical',
  },
  text: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
}));

export default function AnalysisMetadataModal(props: AnalysisMetadataModalProps): JSX.Element {
  const [name, setName] = useState(props.analysis.name);
  const [description, setDescription] = useState(props.analysis.description);
  const [folderId, setFolderId] = useState(props.analysis.folder.id);

  const {classes} = useStyles();

  const writable = props.analysis.permissions.writable;

  const trimmedName = name.trim();
  const isNameBlank = trimmedName === '';

  const normalizedDescription = description?.trim() || null;
  const descriptionLength = normalizedDescription?.length ?? 0;
  const isDescriptionTooLong = descriptionLength > MAX_ANALYSIS_DESCRIPTION_LENGTH;

  function isSubmitDisabled(): boolean {
    return writable && (isNameBlank || isDescriptionTooLong);
  }

  function handleSubmit(): void {
    if (writable) {
      const params: UpdateAnalysisParams = {};
      if (trimmedName !== props.analysis.name) {
        params.name = trimmedName;
      }
      if (folderId !== props.analysis.folder.id) {
        params.folderId = folderId;
      }
      if (normalizedDescription !== props.analysis.description) {
        params.description = normalizedDescription;
      }
      if (Object.keys(params).length > 0) {
        updateAnalysisOptimistically(props.analysis, params);
      }
    }
    props.onRequestSubmit();
  }

  return (
    <Modal
      title="Analysis metadata"
      buttons={[
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: writable ? 'Save' : 'Done',
          disabled: isSubmitDisabled(),
          tooltipTitle: !writable
            ? null
            : isNameBlank
              ? `Analysis name must be non-empty.`
              : isDescriptionTooLong
                ? `Description must be at most ${MAX_ANALYSIS_DESCRIPTION_LENGTH} characters.`
                : null,
        },
      ]}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      expandHeight={true}
    >
      <Row>
        <Col md={6}>
          {writable ? (
            <FormGroup>
              <TextField
                label="Name"
                autoFocus={true}
                value={name}
                hasError={isNameBlank}
                onChange={(e) => {
                  setName(clampEntryName(e.target.value));
                }}
                dataTestAttrs={{component: 'AnalysisMetadataModal', element: 'TextField', key: 'name'}}
              />
            </FormGroup>
          ) : (
            <FormGroup>
              <FieldLabel label="Name" />
              <Text
                className={classes.text}
                {...getTestProps({component: 'AnalysisMetadataModal', element: 'nameText'})}
              >
                {props.analysis.name}
              </Text>
            </FormGroup>
          )}
        </Col>
        <Col md={6}>
          {writable ? (
            <FormGroup>
              <FieldLabel label="Folder" />
              <FolderSelector
                enableRedesign={true}
                showNotebook={true}
                value={folderId}
                onChange={(e: {target: {value: string}}) => {
                  setFolderId(e.target.value);
                }}
                unsetOnFetchFail={true}
              />
            </FormGroup>
          ) : (
            <FormGroup>
              <FieldLabel label="Folder" />
              <Text {...getTestProps({component: 'AnalysisMetadataModal', element: 'folderText'})}>
                {props.analysis.folder.name}
              </Text>
            </FormGroup>
          )}
        </Col>
      </Row>
      {folderId !== props.analysis.folder.id && (
        <>
          <Row>
            <Col md={12}>
              <DataSharingWarningBanner folderId={folderId} />
            </Col>
          </Row>
          <br />
        </>
      )}
      <Row>
        <Col md={12}>
          {writable ? (
            <Textarea
              // Mantine's Textarea sets `resize: none`; the legacy `.modal-body textarea` rule currently
              // overrides it, so we set it explicitly to keep vertical resizing regardless of the modal wrapper.
              classNames={{input: classes.input}}
              inputWrapperOrder={['label', 'input', 'description']}
              label="Description"
              description={`${descriptionLength} of ${MAX_ANALYSIS_DESCRIPTION_LENGTH} allowed characters.`}
              error={isDescriptionTooLong}
              value={description ?? ''}
              onChange={(e) => {
                setDescription(e.currentTarget.value);
              }}
              {...getTestProps({component: 'AnalysisMetadataModal', element: 'descriptionTextarea'})}
            />
          ) : (
            <FormGroup>
              <FieldLabel label="Description" />
              <Text
                className={classes.text}
                {...getTestProps({component: 'AnalysisMetadataModal', element: 'descriptionText'})}
              >
                {props.analysis.description}
              </Text>
            </FormGroup>
          )}
        </Col>
      </Row>
      <br />
      <Row>
        <Col md={6}>
          <FormGroup>
            <FieldLabel label="Creator" />
            <p>{props.analysis.creator.name}</p>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <FieldLabel label="Created at" />
            <p>{readableTime(props.analysis.createdAt)}</p>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <FieldLabel label="Last modified" />
            <p>{capitalize(useFuzzyDate(props.analysis.modifiedAt))}</p>
          </FormGroup>
        </Col>
        {BenchlingConfig.STUDIES_ENABLE && (
          <Col md={6}>
            <FieldLabel label="Studies" />
            <EditableStudiesList studyable={props.analysis} isWritable={writable} />
          </Col>
        )}
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <FieldLabel label="Step count" />
            <p>
              {props.analysis.steps.length} / {BenchlingConfig.PIPELINES_MAX_STEP_COUNT}
            </p>
          </FormGroup>
        </Col>
      </Row>
      <hr />
      <Row>
        <Col md={12}>
          <FormGroup>
            <FieldLabel label="Relevant items" />
            <AnalysisRelevantItemsSection analysisId={props.analysis.id} />
          </FormGroup>
        </Col>
      </Row>
    </Modal>
  );
}
